import React from "react"
import { twMerge } from "tailwind-merge"

import placeholderImage from "../../../../assets/images/profile-photo-placeholder.jpg"
import { groupEventCardDateLabel } from "../../../@core/bookableEventType/bookableEventOccurrence.utils"
import { formatBookableEventDurationLabel } from "../../../@core/bookableEventType/bookableEventType.utils"
import GroupEventCardMetaLine from "../../../@core/bookableEventType/GroupEventCardMetaLine"
import {
  FILESTACK_CDN_WIDTHS,
  ensureFilestackCdnResized,
  filestackUrlFromPhotoHandles
} from "../../../utils/filestackCdn"
import { getServiceDurationRange, getServicePriceRange, formatDurationWithSessions } from "../../../utils/utils"
import { getSectionColorScheme } from "../utils/colorModeUtils"
import { formatFontFamily, formatHeadingFontFamily } from "../utils/fontUtils"
import { normalizeServiceImageObjectFit } from "../utils/serviceCardImageUtils"
import { getThemeShapeClasses, applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import { buildServiceUrl, buildEventTypeUrl, buildPublicPageUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import { ScrollRevealItem } from "./shared/ScrollReveal"
import { CARD_IMAGE_HOVER_CLASSES, CARD_IMAGE_OVERLAY_CLASSES } from "./shared/scrollRevealVariants"
import ServiceCardImage from "./shared/ServiceCardImage"

const LOCATION_KIND_BY_ENUM = {
  0: "office",
  1: "virtual",
  2: "home"
}

const normalizeLocationKind = (kind) => {
  const normalizedKind = String(kind ?? "")
    .trim()
    .toLowerCase()
    .replace(/^:/, "")
  return LOCATION_KIND_BY_ENUM[normalizedKind] || normalizedKind
}

const getServiceTypeLabels = (service, practice) => {
  let locationKinds = (service?.locations || []).map((location) => normalizeLocationKind(location?.kind))

  if (locationKinds.length === 0) {
    const serviceLocationIds = service?.locationIds || service?.location_ids || []
    const serviceLocationIdSet = new Set(serviceLocationIds.map((id) => String(id)))

    locationKinds = (practice?.locations || [])
      .filter((location) => serviceLocationIdSet.has(String(location?.id)))
      .map((location) => normalizeLocationKind(location?.kind))
  }

  const hasVirtual = service?.virtual || locationKinds.includes("virtual")
  const hasInPerson =
    locationKinds.includes("office") ||
    locationKinds.includes("physical") ||
    locationKinds.includes("in_person") ||
    locationKinds.includes("in-person") ||
    locationKinds.includes("physical_location") ||
    locationKinds.includes("office_location") ||
    locationKinds.some((kind) => Boolean(kind) && !["virtual", "home"].includes(kind)) ||
    (locationKinds.length === 0 && service?.virtual === false)
  const hasAtHome = service?.atHome || service?.at_home || locationKinds.includes("home")

  const serviceTypes = []
  if (hasVirtual) serviceTypes.push("Virtual")
  if (hasInPerson) serviceTypes.push("In-person")
  if (hasAtHome) serviceTypes.push("At your home")

  if (serviceTypes.length === 0) {
    serviceTypes.push(service?.virtual ? "Virtual" : "In-person")
  }

  return serviceTypes
}

const renderItemDurationMeta = (item, { duration, locationInfo }, practice, { className, style }) => {
  const dateLabel = item.itemType === "event" ? groupEventCardDateLabel(item, practice) : ""
  if (!duration && !locationInfo && !dateLabel) return null

  if (item.itemType === "event") {
    return (
      <GroupEventCardMetaLine
        duration={duration}
        locationInfo={locationInfo}
        dateLabel={dateLabel}
        className={className}
        style={style}
      />
    )
  }

  return (
    <div className={className} style={style}>
      {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
    </div>
  )
}

const ServicesSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "list"
  const selectedServiceIds = settings.selectedServiceIds || []

  // Get color scheme based on color mode
  const colorScheme = getSectionColorScheme(settings, theme)

  // Use color scheme for colors
  const sectionBackgroundColor = colorScheme.sectionBackground
  const bodyColor = colorScheme.bodyColor

  // CTA Button settings
  const showButton = settings.showButton || false
  const buttonText = settings.buttonText || "Book Now"
  const buttonAction = settings.buttonAction || "service"
  const buttonTarget = settings.buttonTarget || ""
  const imageObjectFit = normalizeServiceImageObjectFit(settings.imageObjectFit)

  const handleItemClick = (item) => {
    if (item.itemType === "service" || item.itemType === "package") {
      window.location.href = buildServiceUrl(item, practice)
    } else if (item.itemType === "event") {
      // For event instances, use the slug from bookableEventTypeSlug if available
      const eventSlug = item.bookableEventTypeSlug || item.slug
      window.location.href = buildEventTypeUrl(eventSlug, practice)
    }
  }

  const handleCTAClick = () => {
    switch (buttonAction) {
      case "page":
        if (buttonTarget) {
          window.location.href = buildPublicPageUrl(buttonTarget, practice)
        }
        break
      case "service":
        if (buttonTarget) {
          window.location.href = buildServiceUrl(buttonTarget, practice)
        }
        break
      case "external":
        if (buttonTarget) {
          // Sanitize URL: ensure it starts with http:// or https://
          let sanitizedUrl = buttonTarget.trim()
          if (!sanitizedUrl.startsWith("http://") && !sanitizedUrl.startsWith("https://")) {
            sanitizedUrl = `https://${sanitizedUrl}`
          }
          // Additional validation: check if URL is valid
          try {
            new URL(sanitizedUrl)
            window.open(sanitizedUrl, "_blank", "noopener,noreferrer")
          } catch (e) {
            // Invalid URL - silently fail
          }
        }
        break
      default:
        break
    }
  }

  // Get all available items
  // Services includes both services and packages (packages have package: true)
  const allServices = (practice?.services || []).filter((service) => service.public === true)
  const allPracticeServices = allServices.filter((service) => !service.package)
  const allPracticePackages = allServices.filter((service) => service.package === true)
  // Events can be either event types (templates) or specific event instances
  const allPracticeEventTypes = practice?.bookableEventTypes || []
  const allPracticeEventInstances = practice?.bookableEvents || []
  const allPracticeEvents = [...allPracticeEventTypes, ...allPracticeEventInstances]

  // Only show items that are explicitly selected (selectedItemIds)
  // Parse the selectedItemIds to handle the new unified format: "service-123", "package-456", "event-slug"
  const selectedItemIds = settings.selectedItemIds || selectedServiceIds || []
  const selectedItems = selectedItemIds
    .map((id) => {
      // Handle both old format (just ID) and new format (type-id)
      if (typeof id === "string" && id.includes("-")) {
        const [type, itemId] = id.split("-")
        if (type === "service") {
          const service = allPracticeServices.find((s) => s.id?.toString() === itemId)
          return service ? { ...service, itemType: "service" } : null
        }
        if (type === "package") {
          const pkg = allPracticePackages.find((p) => p.id?.toString() === itemId)
          return pkg ? { ...pkg, itemType: "package" } : null
        }
        if (type === "event") {
          // Events can have either numeric ID or slug
          const event = allPracticeEvents.find((e) => e.id?.toString() === itemId || e.slug === itemId)
          return event ? { ...event, itemType: "event" } : null
        }
        return null
      }
      // Old format: just the service ID
      const service = allPracticeServices.find((s) => s.id === id)
      return service ? { ...service, itemType: "service" } : null
    })
    .filter(Boolean)

  // Show all selected items (no limits)
  const allItems = selectedItems

  // Disable animation on mobile when there are more than 5 services to prevent performance issues
  const shouldDisableAnimation = allItems.length > 5

  // Render item card for mobile (vertical stack with image on top)
  const renderMobileServiceCard = (item, index) => {
    // Get display info based on item type
    let price = ""
    let duration = ""
    let locationInfo = ""
    let displayName = item.name || item.title
    let displayDescription = item.description || ""

    if (item.itemType === "service") {
      price = getServicePriceRange(item, practice?.defaultCurrency || "USD")
      duration = getServiceDurationRange(item)
      const serviceTypes = getServiceTypeLabels(item, practice)
      locationInfo = serviceTypes.filter(Boolean).join(" • ")
    } else if (item.itemType === "package") {
      price = getServicePriceRange(item, practice?.defaultCurrency || "USD")
      const timeLength =
        typeof item.timeLength === "number" ? item.timeLength : item.timeLength?.value || item.timeLength
      duration = timeLength ? formatDurationWithSessions(timeLength, item) : ""
      locationInfo = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    } else if (item.itemType === "event") {
      // Events send amount_cents, not price
      price = item.amount_cents ? `$${(item.amount_cents / 100).toFixed(2)}` : item.price ? `$${item.price}` : ""
      duration = formatBookableEventDurationLabel(item)
      const virtual = item.kind === "virtual" || item.virtual ? "Virtual" : "In-person"
      const capacity = item.capacity ? `${item.capacity} capacity` : ""
      locationInfo = [virtual, capacity].filter(Boolean).join(" • ")
    }

    // Get image URL from various possible sources
    // Check both camelCase and snake_case properties (backend transforms keys to camelCase)
    const photoHandles = item.photoHandles || item.filestackPhoto || item.filestack_photo
    const imageUrl = ensureFilestackCdnResized(
      item.imageUrl ||
        item.mediumWebpUrl ||
        item.photoUrl ||
        item.photo_url ||
        filestackUrlFromPhotoHandles(photoHandles, { width: FILESTACK_CDN_WIDTHS.card }),
      { width: FILESTACK_CDN_WIDTHS.card }
    )

    // Truncate description for display
    if (displayDescription && displayDescription.length > 200) {
      displayDescription = displayDescription.substring(0, 200) + "..."
    }

    const cardClassName = "group flex h-[500px] w-full flex-col items-start justify-start gap-4"
    const cardBody = (
      <>
        <div
          className={twMerge(
            "relative aspect-[396/250] w-full overflow-hidden bg-gray-100",
            getThemeShapeClasses(theme)
          )}>
          <ServiceCardImage
            src={imageUrl || placeholderImage}
            alt={displayName}
            objectFit={imageObjectFit}
            hoverClassName={CARD_IMAGE_HOVER_CLASSES}
            onError={(e) => {
              if (e.target.src !== placeholderImage) e.target.src = placeholderImage
            }}
          />
          <div className={CARD_IMAGE_OVERLAY_CLASSES} style={{ backgroundColor: "rgba(0,0,0,0.06)" }} />
        </div>

        {/* Content - Fixed structure with consistent height - Left aligned text */}
        <div className="flex w-full flex-1 flex-col items-start justify-start gap-2 overflow-hidden text-left">
          {/* Price and Badge - First */}
          <div className="flex w-full items-center gap-3">
            {price && (
              <div
                className="truncate text-[18px] font-semibold leading-[24px]"
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                  color: bodyColor
                }}>
                {price}
              </div>
            )}
            {/* Package Badge */}
            {item.itemType === "package" && (
              <span
                className="shrink-0 rounded-full text-xs font-semibold uppercase tracking-wider"
                style={{
                  backgroundColor: theme?.colors?.primary || "#BD654F",
                  color: "#FFFFFF",
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  borderRadius: theme?.buttonShape?.borderRadius || "9999px",
                  padding: "0.25rem 0.75rem"
                }}>
                {item.numberOfSessions ? `${item.numberOfSessions} Session Package` : "Package"}
              </span>
            )}
            {/* Event Badge */}
            {item.itemType === "event" && (
              <span
                className="shrink-0 rounded-full text-xs font-semibold uppercase tracking-wider"
                style={{
                  backgroundColor: theme?.colors?.primary || "#BD654F",
                  color: "#FFFFFF",
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  borderRadius: theme?.buttonShape?.borderRadius || "9999px",
                  padding: "0.25rem 0.75rem"
                }}>
                Group Event
              </span>
            )}
          </div>

          {/* Title - Second - Single line with ellipsis */}
          <div className="w-full">
            <div
              className="w-full truncate text-[22px] font-bold leading-[32px]"
              style={{
                fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: bodyColor
              }}>
              {displayName}
            </div>
          </div>

          {/* Duration and Location - Third */}
          {renderItemDurationMeta(item, { duration, locationInfo }, practice, {
            className: "w-full text-[14px] font-normal leading-[20px]",
            style: {
              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
              fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
              color: bodyColor
            }
          })}

          {/* Description - Fourth - Limited to 2 lines */}
          <div className="w-full">
            {displayDescription && (
              <div
                className="prose prose-lg line-clamp-2 w-full max-w-none text-[16px] font-normal leading-[24px]"
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                  color: bodyColor,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                }}
                dangerouslySetInnerHTML={{ __html: displayDescription }}></div>
            )}
          </div>

          {/* Book Now button - Close to content */}
          <div
            className="mt-4 flex cursor-pointer items-center justify-start gap-3 px-8 py-3 transition-opacity hover:opacity-90"
            style={{
              backgroundColor: theme?.colors?.primary || "#BD654F",
              borderRadius: theme?.borderRadius || "12px"
            }}
            onClick={() => handleItemClick(item)}>
            <div
              className="text-[16px] font-normal leading-[24px] text-white"
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.bgLight || "#F4ECE9"
              }}>
              Book Now
            </div>
          </div>
        </div>
      </>
    )

    if (shouldDisableAnimation) {
      return (
        <div key={index} className={cardClassName}>
          {cardBody}
        </div>
      )
    }

    return (
      <ScrollRevealItem key={index} staggerIndex={index + 1} className={cardClassName}>
        {cardBody}
      </ScrollRevealItem>
    )
  }

  const sectionContent = (
    <div className="flex w-full flex-col items-start gap-8">
      <ScrollRevealItem
        staggerIndex={0}
        disabled={shouldDisableAnimation}
        className="flex w-full flex-col items-start gap-8">
        {/* Title and Browse Services Button */}
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full items-center justify-between gap-4">
            <h2
              className="text-left text-[28px] font-bold leading-[32px]"
              style={{
                fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: bodyColor
              }}>
              {section.headline || "Services"}
            </h2>

            {/* CTA Button - at title level */}
            {showButton && (
              <button
                onClick={handleCTAClick}
                className={`inline-flex shrink-0 items-center justify-center gap-3 px-6 py-2 transition-opacity hover:opacity-90 ${getButtonClasses(
                  theme?.buttonStyle
                )}`}
                style={applyButtonTheme(
                  {
                    backgroundColor: theme?.colors?.secondary || "#E6DAD7",
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                    color: bodyColor,
                    fontSize: "14px",
                    lineHeight: "20px"
                  },
                  theme
                )}>
                {buttonText}
              </button>
            )}
          </div>

          {section.body && (
            <p
              className="max-w-3xl text-left text-[18px]"
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor,
                lineHeight: "1.75rem" // 28px
              }}>
              {section.body}
            </p>
          )}
        </div>
      </ScrollRevealItem>

      {/* Services Grid */}
      {allItems.length > 0 && (
        <div className="w-full">
          {layout === "highlights_2" || layout === "highlights_3" ? (
            // Highlights layout - grid for mobile with equal height
            <div
              className={`grid items-stretch gap-6 ${
                layout === "highlights_2" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"
              }`}>
              {allItems.map((item, index) => renderMobileServiceCard(item, index))}
            </div>
          ) : (
            // List layout - vertical stack
            <div className="flex flex-col gap-10">
              {allItems.map((item, index) => renderMobileServiceCard(item, index))}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {allItems.length === 0 && (
        <div className="flex flex-col items-center gap-6 py-16 text-center">
          <div className="text-6xl">🔧</div>
          <h3
            className="text-2xl font-semibold"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              color: theme?.colors?.textDark || "#55433F"
            }}>
            No services available
          </h3>
          <p
            className="text-lg"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              color: bodyColor
            }}>
            {practice?.services?.length === 0
              ? "This practice hasn't added any services yet."
              : "No services match the current selection."}
          </p>
        </div>
      )}

      {/* Show More Button - Not needed for Services section (shows all services) */}
    </div>
  )

  return (
    <section
      className={twMerge("relative py-8 pb-[72px]", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: sectionBackgroundColor
      }}>
      <div className="mx-auto max-w-[1160px] px-5">{sectionContent}</div>
    </section>
  )
}

export default ServicesSectionMobile
